import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';
import { emailSchema, simplePasswordSchema } from '../../lib/zodHelpers';

import Button from '../../components/Button/Button';
import Loader from '../../components/Loader/Loader';

import { WarningIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const initialValues = {
  email: '',
  password: '',
};

const LoginForm = ({
  onSubmit = EMPTY_FUNCTION,
  buttonText = '',
  error = '',
  testId = '',
}) => {
  const { t } = useTranslation();
  const [errorFormCatch, setErrorFormCatch] = useState(error);
  const [isLogging, setIsLogging] = useState(false);

  const validationSchema = useMemo(
    () =>
      z.object({
        email: emailSchema(t),
        password: simplePasswordSchema(t),
      }),
    [t],
  );

  const onFormSubmit = useCallback(
    async ({ value, formApi }) => {
      setIsLogging(true);
      const [, errors] = await onSubmit(value);

      if (errors && Object.keys(errors).length > 0) {
        Object.entries(errors).forEach(([key, error]) => {
          if (key === 'global') {
            setErrorFormCatch(error);
          } else if (['email', 'password'].includes(key)) {
            formApi.setFieldMeta(key, (prev) => ({
              ...prev,
              errorMap: { onSubmit: error },
            }));
          }
        });
      }

      setIsLogging(false);
    },
    [onSubmit],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: onFormSubmit,
    validators: {
      onSubmit: validationSchema,
    },
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id={'authentication-from'}
        className="w-full"
        onSubmit={handleSubmit}
        noValidate={true}
        {...getTestProps(testId)}
      >
        <div
          className="flex flex-wrap mb-6"
          {...getTestProps(testId, 'email-container')}
        >
          <form.AppField name="email">
            {(field) => <field.Input placeholder={t('Global.Email')} />}
          </form.AppField>
        </div>

        <div
          className="flex flex-wrap"
          {...getTestProps(testId, 'password-container')}
        >
          <form.AppField name="password">
            {(field) => (
              <field.Input type="password" placeholder={t('Global.Password')} />
            )}
          </form.AppField>
        </div>

        <div className="flex flex-wrap my-8">
          <div className="w-full m-auto text-center text-blue">
            <Link to="/change-password-request">
              {t('Form.FormLoginForgotYourPassword')}
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap">
          <div className="w-full flex flex-col justify-center text-center">
            <Button
              buttonSize="lg"
              type="submit"
              disabled={isLogging}
              iconImage={
                isLogging ? <Loader size="small" type="spinner-grid" /> : null
              }
              additionalClasses="min-w-[300px] justify-center"
            >
              {t('Global.LogIn')}
            </Button>
            {buttonText && <div className="text-blue py-2">{buttonText}</div>}
            {errorFormCatch && (
              <div className="text-red py-2 inline-flex">
                <div className="h-full">
                  <WarningIcon className="h-5 w-5" />
                </div>
                <div className="block text-left pl-2">{errorFormCatch}</div>
              </div>
            )}
          </div>
        </div>
      </form>
    </form.AppForm>
  );
};

export default LoginForm;

LoginForm.propTypes = {
  /**
   * LoginForm on submit handler
   */
  onSubmit: PropTypes.func,
  /**
   * Text under button
   */
  buttonText: PropTypes.node,
  /**
   * Error text under button
   */
  error: PropTypes.node,
  /**
   * Form test id
   */
  testId: PropTypes.string,
};
